import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { ArrowLeft, Sparkle } from '@phosphor-icons/react';

/**
 * WorldEventDetail — full-screen "tap a world event for context" page.
 * Currently shows the event metadata + flavor text. Later we'll add
 * "what's happening inside" (affected bubbles, lighthouses, blooms).
 */
const WorldEventDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const [event, setEvent] = useState(null);

  useEffect(() => {
    api.get(`/world/events/${id}`).then(({ data }) => setEvent(data.event)).catch(() => nav('/'));
  }, [id, nav]);

  if (!event) return <div className="text-center py-20 text-white/60">listening to the world…</div>;

  const minsLeft = Math.max(0, Math.round((new Date(event.ends_at) - Date.now()) / 60000));
  const palette = {
    aurora_drift:    'from-bx-legendary/40 to-bx-cyan/30',
    bubble_storm:    'from-bx-cyan/40 to-bx-magenta/20',
    midnight_bloom:  'from-bx-magenta/30 to-bx-volt/20',
    quiet_hour:      'from-bx-volt/20 to-bx-cyan/15',
    sunset_migration:'from-bx-gold/30 to-bx-magenta/20',
  }[event.kind] || 'from-white/10 to-transparent';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-14 pb-32" data-testid="world-event-page">
      <button onClick={() => nav(-1)} className="text-xs uppercase tracking-widest text-white/55 mb-6 inline-flex items-center gap-1">
        <ArrowLeft size={12} weight="bold" /> back
      </button>
      <div className={`grad-border p-0 overflow-hidden rounded-3xl`}>
        <div className={`aspect-[16/8] relative bg-gradient-to-br ${palette}`}>
          <div className="absolute inset-0 grid place-items-center">
            <div className="text-7xl sm:text-9xl breathe">{event.glyph}</div>
          </div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent" />
        </div>
        <div className="p-6 sm:p-8">
          <div className="text-[10px] uppercase tracking-[0.4em] text-bx-cyan">world event · happening now</div>
          <h1 className="font-display text-4xl sm:text-6xl font-black tracking-tighter mt-2">{event.label}</h1>
          <div className="text-xs text-white/55 mt-2">
            {event.region ? `region · ${event.region.toUpperCase()}` : 'across the whole sky'} · {minsLeft} min left
          </div>
          {event.flavor && (
            <blockquote className="mt-5 text-base italic font-display text-white/90 border-l-2 border-bx-cyan/60 pl-3">
              "{event.flavor}"
            </blockquote>
          )}
          <div className="mt-6 grid grid-cols-3 gap-3 text-center">
            <div className="glass-hi rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-white/60">encounters</div>
              <div className="font-display text-xl font-black text-bx-volt mt-1">×{event.encounter_x?.toFixed(1)}</div>
            </div>
            <div className="glass-hi rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-white/60">tips</div>
              <div className="font-display text-xl font-black text-bx-gold mt-1">×{(event.tip_x || 1).toFixed(1)}</div>
            </div>
            <div className="glass-hi rounded-2xl p-3">
              <div className="text-[10px] uppercase tracking-widest text-white/60">mood</div>
              <div className="font-display text-base font-black text-bx-cyan mt-1 capitalize flex items-center justify-center gap-1">
                <Sparkle size={12} weight="fill" /> {event.mood}
              </div>
            </div>
          </div>
          <p className="text-xs text-white/55 mt-6 italic">
            Bubbles within this region will feel different until {new Date(event.ends_at).toLocaleTimeString([], {hour:'numeric',minute:'2-digit'})}.
            Every bubble's diary may mention what they saw.
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorldEventDetail;
